import React, { useState } from 'react';
import { 
  Check, X, Sparkles, ShieldCheck, DollarSign, Laptop, 
  HelpCircle, ChevronRight, Zap, ArrowUpRight, Cpu, Layers,
  Headphones, Mic, Volume2, Activity, Smartphone
} from 'lucide-react';
import { ClarvosCTAButton, ClarvosPillBadge } from './ClarvosBrand';

interface ComparisonFeature {
  name: string;
  category: 'hardware' | 'clinical' | 'aac' | 'infrastructure' | 'cost';
  description: string;
  abilityOS: { supported: boolean; value: string; highlight?: boolean };
  drSpeech: { supported: boolean; value: string };
  tdSnap: { supported: boolean; value: string };
  audiometerHw: { supported: boolean; value: string };
  proloquo: { supported: boolean; value: string };
  centralReach: { supported: boolean; value: string };
}

const COMPARISON_FEATURES: ComparisonFeature[] = [
  {
    name: 'Hardware Requirement',
    category: 'hardware',
    description: 'Specialized proprietary physical hardware or USB security dongles required to operate.',
    abilityOS: { supported: true, value: '100% Commodity PC/Mac/Tablet/Phone (Zero Hardware)', highlight: true },
    drSpeech: { supported: false, value: 'Proprietary USB Dongle & DSP Box ($3,200)' },
    tdSnap: { supported: false, value: 'Tobii I-Series Hardware ($8,500+)' },
    audiometerHw: { supported: false, value: 'Dedicated Pure-Tone Audiometer Unit ($4,500–$9,000)' },
    proloquo: { supported: false, value: 'iPad / iOS Only ($250 app + $400 iPad)' },
    centralReach: { supported: true, value: 'Web Cloud (No clinical therapy tools)' }
  },
  {
    name: 'Dual-Screen Speech Waveform Biofeedback',
    category: 'clinical',
    description: 'Upper clinician reference wave vs real-time patient microphone Web Audio FFT match %.',
    abilityOS: { supported: true, value: 'Built-in Real-Time FFT & Spectral Match %', highlight: true },
    drSpeech: { supported: true, value: 'Included (Desktop Only, Windows 98/XP era UI)' },
    tdSnap: { supported: false, value: 'Not Supported' },
    audiometerHw: { supported: false, value: 'Not Supported' },
    proloquo: { supported: false, value: 'Not Supported' },
    centralReach: { supported: false, value: 'Not Supported' }
  },
  {
    name: 'Real-Time Voice Balloon / Pitch Game',
    category: 'clinical',
    description: 'Motivating pediatric biofeedback game elevating graphics based on vocal pitch & volume.',
    abilityOS: { supported: true, value: 'Built-in 2D Balloon Biofeedback Gym', highlight: true },
    drSpeech: { supported: true, value: 'Included in DRS Suite' },
    tdSnap: { supported: false, value: 'Not Supported' },
    audiometerHw: { supported: false, value: 'Not Supported' },
    proloquo: { supported: false, value: 'Not Supported' },
    centralReach: { supported: false, value: 'Not Supported' }
  },
  {
    name: 'Diagnostic Pure-Tone Audiometer & Speech Banana',
    category: 'clinical',
    description: 'Calibrated octave tone testing (250–8000Hz), Left/Right stereo panning, and interactive Audiogram.',
    abilityOS: { supported: true, value: 'Calibrated Sine Wave Generator & Speech Banana Graph', highlight: true },
    drSpeech: { supported: false, value: 'Not Supported (Speech Only)' },
    tdSnap: { supported: false, value: 'Not Supported' },
    audiometerHw: { supported: true, value: 'Dedicated Hardware Device ($6,500)' },
    proloquo: { supported: false, value: 'Not Supported' },
    centralReach: { supported: false, value: 'Not Supported' }
  },
  {
    name: 'Digital Articulation Mirror & Oral-Motor Gym',
    category: 'clinical',
    description: 'WebRTC front-camera self-view with target anatomical guides for /r/, /s/, /th/ sounds.',
    abilityOS: { supported: true, value: 'Built-in WebRTC Mirror & Repetition Drills', highlight: true },
    drSpeech: { supported: false, value: 'Requires Physical Wall Mirror ($800)' },
    tdSnap: { supported: false, value: 'Not Supported' },
    audiometerHw: { supported: false, value: 'Not Supported' },
    proloquo: { supported: false, value: 'Not Supported' },
    centralReach: { supported: false, value: 'Not Supported' }
  },
  {
    name: 'AAC Motor Planning & Contextual Grammar',
    category: 'aac',
    description: 'Fitzgerald key color-coded grid with consistent motor locations & inflection pills.',
    abilityOS: { supported: true, value: 'Core Vocab, Motor Planning, & Morphology Pills', highlight: true },
    drSpeech: { supported: false, value: 'No AAC Functionality' },
    tdSnap: { supported: true, value: 'TD Snap Core First ($49/yr or $300)' },
    audiometerHw: { supported: false, value: 'Not Supported' },
    proloquo: { supported: true, value: 'Proloquo2Go ($249 one-time)' },
    centralReach: { supported: false, value: 'No Direct AAC Board' }
  },
  {
    name: 'Disability-Centric Taxonomy (RPwD / DSM-5)',
    category: 'clinical',
    description: 'Instant filtering and tailored sensory adaptations across 8 diagnostic clusters.',
    abilityOS: { supported: true, value: '8 Clinical Wings Mapped to RPwD 2016 & DSM-5', highlight: true },
    drSpeech: { supported: false, value: 'Uncategorized' },
    tdSnap: { supported: false, value: 'General AAC' },
    audiometerHw: { supported: false, value: 'Audiology Only' },
    proloquo: { supported: false, value: 'General AAC' },
    centralReach: { supported: false, value: 'ABA Centric' }
  },
  {
    name: '1-Switch & 2-Switch Scanning Engine',
    category: 'infrastructure',
    description: 'Automated 1-switch auto-scan and 2-switch step-scan with audio click earcons.',
    abilityOS: { supported: true, value: 'Built-in Universal Switch Scanning Engine', highlight: true },
    drSpeech: { supported: false, value: 'Keyboard / Mouse Only' },
    tdSnap: { supported: true, value: 'Supported (Requires Tobii Switch Box)' },
    audiometerHw: { supported: false, value: 'Manual Clinician Response Button' },
    proloquo: { supported: true, value: 'iOS Switch Control' },
    centralReach: { supported: false, value: 'Mouse / Touch Only' }
  },
  {
    name: 'Cerebral Palsy Touch Dwell & Tremor Filter',
    category: 'infrastructure',
    description: 'Radial visual countdown (100–1200ms) that discards accidental spastic double-taps.',
    abilityOS: { supported: true, value: 'Adjustable Touch Acceptance Delay Engine', highlight: true },
    drSpeech: { supported: false, value: 'Not Supported' },
    tdSnap: { supported: true, value: 'Supported' },
    audiometerHw: { supported: false, value: 'Not Supported' },
    proloquo: { supported: false, value: 'Limited iOS Accessibility' },
    centralReach: { supported: false, value: 'Not Supported' }
  },
  {
    name: 'Offline-First PWA & Native Multi-Platform',
    category: 'infrastructure',
    description: 'Installable PWA, Android APK, Windows EXE, Mac DMG with Dexie.js offline caching.',
    abilityOS: { supported: true, value: 'Zero-Install Web + Android + Win + Mac Offline', highlight: true },
    drSpeech: { supported: false, value: 'Legacy Windows PC Only' },
    tdSnap: { supported: false, value: 'Windows & iPad Only' },
    audiometerHw: { supported: false, value: 'Standalone Firmware Box' },
    proloquo: { supported: false, value: 'iPad / iOS Only' },
    centralReach: { supported: true, value: 'Web Cloud Only (Requires Active Internet)' }
  },
  {
    name: '3-Year Total Cost of Ownership (1 School / 25 Students)',
    category: 'cost',
    description: 'Total equipment acquisition, software licenses, maintenance, and hardware replacements.',
    abilityOS: { supported: true, value: 'Included in AbilityOS License ($0 Extra Hardware)', highlight: true },
    drSpeech: { supported: false, value: '$3,200 (Single PC workstation)' },
    tdSnap: { supported: false, value: '$12,500 (With eye gaze / speech hardware)' },
    audiometerHw: { supported: false, value: '$7,000 (Calibration & hardware maintenance)' },
    proloquo: { supported: false, value: '$16,250 (25 iPads @ $400 + $250 app)' },
    centralReach: { supported: false, value: '$18,000+ ($60/user/mo EHR platform fee)' }
  }
];

interface ClinicalComparisonChartProps {
  onExploreDemo?: (tab: string) => void;
  onSelectModule?: (moduleId: string) => void;
}

export const ClinicalComparisonChart: React.FC<ClinicalComparisonChartProps> = ({
  onExploreDemo,
  onSelectModule
}) => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'clinical' | 'hardware' | 'infrastructure' | 'cost'>('all');
  const [mobileCompareTarget, setMobileCompareTarget] = useState<'drSpeech' | 'tdSnap' | 'audiometerHw' | 'proloquo' | 'centralReach'>('drSpeech');

  const filteredFeatures = activeCategory === 'all'
    ? COMPARISON_FEATURES
    : COMPARISON_FEATURES.filter(f => f.category === activeCategory);

  const competitorMeta = {
    drSpeech: { name: 'Tiger DRS Dr. Speech', tag: 'Acoustic Suite', hardwareReq: '$3,200' },
    tdSnap: { name: 'Tobii Dynavox TD Snap', tag: 'AAC Hardware', hardwareReq: '$8,500+' },
    audiometerHw: { name: 'Diagnostic Audiometer', tag: 'Clinical Hardware', hardwareReq: '$6,500' },
    proloquo: { name: 'Proloquo2Go', tag: 'iOS Only App', hardwareReq: '$650/seat' },
    centralReach: { name: 'CentralReach EHR', tag: 'Practice Cloud', hardwareReq: '$18,000/yr' }
  };

  return (
    <section id="comparison" className="py-20 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
      
      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1c1b1f]/5 border border-[#1c1b1f]/10 text-xs font-mono font-bold uppercase tracking-wider text-[#1c1b1f] mb-4">
          <Sparkles className="w-3.5 h-3.5 text-[#ffd64e] fill-current" />
          Clinical Benchmarking & TCO Matrix
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1c1b1f] tracking-tight leading-tight mb-4">
          AbilityOS vs. Traditional <br className="hidden sm:inline" />
          <span className="text-[#1c1b1f] underline decoration-[#ffd64e] decoration-wavy decoration-2">Hardware & Isolated Apps</span>
        </h2>

        <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
          Special education centers and hospitals typically spend over <strong>$35,000+</strong> procuring fragmented speech workstations, diagnostic audiometers, and iPad apps. AbilityOS unites them all into a <strong>100% hardware-free software suite</strong> running on standard consumer devices.
        </p>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
          {[
            { id: 'all', label: 'All Capabilities' },
            { id: 'clinical', label: 'Dr. Speech & Audiology' },
            { id: 'hardware', label: 'Hardware Freedom' },
            { id: 'infrastructure', label: 'Motor & Switch Access' },
            { id: 'cost', label: 'Cost & TCO' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id as any)}
              className={`px-4 py-2 rounded-full text-xs font-black transition-all cursor-pointer border ${
                activeCategory === tab.id
                  ? 'bg-[#1c1b1f] text-white border-[#1c1b1f] shadow-md'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200 shadow-xs'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 1. DESKTOP & TABLET VIEW: FULL MATRIX TABLE                           */}
      {/* ===================================================================== */}
      <div className="hidden lg:block bg-white rounded-[32px] border border-slate-200 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-[#1c1b1f] text-white text-xs">
                <th className="py-5 px-6 font-extrabold tracking-wide w-1/4">
                  Clinical & Assistive Capability
                </th>

                {/* AbilityOS Featured Column Header */}
                <th className="py-5 px-6 font-black bg-[#ffd64e] text-black w-1/5 shadow-inner">
                  <div className="flex items-center gap-2">
                    <span className="text-sm uppercase tracking-wider">AbilityOS</span>
                    <span className="px-2 py-0.5 rounded-full bg-black text-[#ffd64e] text-[9px] font-mono font-black">
                      ALL-IN-ONE
                    </span>
                  </div>
                  <div className="text-[10px] font-bold text-black/80 mt-0.5">
                    100% Hardware-Free
                  </div>
                </th>

                <th className="py-5 px-4 font-bold text-slate-300">
                  <div>Tiger DRS Dr. Speech</div>
                  <div className="text-[10px] text-slate-400 font-mono">Legacy Workstation</div>
                </th>

                <th className="py-5 px-4 font-bold text-slate-300">
                  <div>Tobii Dynavox TD Snap</div>
                  <div className="text-[10px] text-slate-400 font-mono">AAC Hardware</div>
                </th>

                <th className="py-5 px-4 font-bold text-slate-300">
                  <div>Pure-Tone Audiometer</div>
                  <div className="text-[10px] text-slate-400 font-mono">Clinical Hardware</div>
                </th>

                <th className="py-5 px-4 font-bold text-slate-300">
                  <div>Proloquo2Go</div>
                  <div className="text-[10px] text-slate-400 font-mono">iOS-Only App</div>
                </th>

                <th className="py-5 px-4 font-bold text-slate-300">
                  <div>CentralReach EHR</div>
                  <div className="text-[10px] text-slate-400 font-mono">Practice Cloud</div>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-xs">
              {filteredFeatures.map((feat, idx) => (
                <tr 
                  key={idx}
                  className={`hover:bg-slate-50/80 transition-colors ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-[#fcfaf7]'
                  }`}
                >
                  {/* Feature Title & Description */}
                  <td className="py-4 px-6 align-top">
                    <div className="font-extrabold text-[#1c1b1f] text-sm mb-0.5">
                      {feat.name}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-snug">
                      {feat.description}
                    </div>
                  </td>

                  {/* AbilityOS Column (Highlighted) */}
                  <td className="py-4 px-6 align-top bg-[#ffd64e]/10 border-x border-[#ffd64e]/30">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                      <span className="font-black text-[#1c1b1f] text-xs leading-snug">
                        {feat.abilityOS.value}
                      </span>
                    </div>
                  </td>

                  {/* Tiger DRS Dr. Speech */}
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-start gap-1.5">
                      {feat.drSpeech.supported ? (
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      )}
                      <span className="text-slate-600 text-[11px] leading-tight">
                        {feat.drSpeech.value}
                      </span>
                    </div>
                  </td>

                  {/* Tobii Dynavox TD Snap */}
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-start gap-1.5">
                      {feat.tdSnap.supported ? (
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      )}
                      <span className="text-slate-600 text-[11px] leading-tight">
                        {feat.tdSnap.value}
                      </span>
                    </div>
                  </td>

                  {/* Pure-Tone Audiometer Hardware */}
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-start gap-1.5">
                      {feat.audiometerHw.supported ? (
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      )}
                      <span className="text-slate-600 text-[11px] leading-tight">
                        {feat.audiometerHw.value}
                      </span>
                    </div>
                  </td>

                  {/* Proloquo2Go */}
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-start gap-1.5">
                      {feat.proloquo.supported ? (
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      )}
                      <span className="text-slate-600 text-[11px] leading-tight">
                        {feat.proloquo.value}
                      </span>
                    </div>
                  </td>

                  {/* CentralReach */}
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-start gap-1.5">
                      {feat.centralReach.supported ? (
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      )}
                      <span className="text-slate-600 text-[11px] leading-tight">
                        {feat.centralReach.value}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Table Bottom Quick Launch Bar */}
        <div className="bg-[#1c1b1f] text-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#ffd64e] text-black rounded-full font-black text-sm">
              ★
            </div>
            <div>
              <div className="text-sm font-extrabold">Ready to replace fragmented hardware?</div>
              <div className="text-xs text-slate-300">
                Test the Dr. Speech studio, Audiometer with Speech Banana, and AAC board right now.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onExploreDemo && onExploreDemo('modules')}
              className="px-6 py-3 rounded-full bg-[#ffd64e] hover:bg-[#ffd64e]/90 text-black font-extrabold text-xs flex items-center gap-2 cursor-pointer shadow-lg transition-all"
            >
              <span>Launch Live Interactive Sandbox</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 2. MOBILE & TABLET COMPACT VIEW: 1-ON-1 COMPETITOR COMPARISON CARDS    */}
      {/* ===================================================================== */}
      <div className="block lg:hidden space-y-6">
        
        {/* Mobile Competitor Selector Pill Bar */}
        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">
            Compare AbilityOS Directly Against:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(competitorMeta).map(([k, meta]) => (
              <button
                key={k}
                onClick={() => setMobileCompareTarget(k as any)}
                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer border ${
                  mobileCompareTarget === k
                    ? 'bg-[#1c1b1f] text-white border-[#1c1b1f] shadow-sm'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200'
                }`}
              >
                {meta.name}
              </button>
            ))}
          </div>
        </div>

        {/* Competitor Profile Banner */}
        <div className="bg-[#1c1b1f] text-white p-5 rounded-2xl flex items-center justify-between shadow-md">
          <div>
            <div className="text-[10px] uppercase font-mono text-[#ffd64e] font-bold">1-on-1 Head to Head</div>
            <h4 className="text-lg font-black text-white">AbilityOS vs. {competitorMeta[mobileCompareTarget].name}</h4>
            <div className="text-xs text-slate-400 mt-0.5">Competitor Hardware Overhead: <span className="font-bold text-rose-400">{competitorMeta[mobileCompareTarget].hardwareReq}</span></div>
          </div>
          <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono font-black rounded-full">
            100% Free of HW
          </span>
        </div>

        {/* Feature Comparison Cards */}
        <div className="space-y-3">
          {filteredFeatures.map((feat, idx) => {
            const compVal = feat[mobileCompareTarget];
            return (
              <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                <div>
                  <div className="text-sm font-extrabold text-[#1c1b1f]">{feat.name}</div>
                  <div className="text-xs text-slate-500">{feat.description}</div>
                </div>

                {/* Side-by-side Mobile Comparison */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                  {/* AbilityOS side */}
                  <div className="bg-[#ffd64e]/15 p-3 rounded-xl border border-[#ffd64e]/30 space-y-1">
                    <div className="flex items-center gap-1.5 text-black font-extrabold text-[11px]">
                      <span className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px]">✓</span>
                      <span>AbilityOS</span>
                    </div>
                    <p className="text-[#1c1b1f] font-bold text-[11px] leading-snug">
                      {feat.abilityOS.value}
                    </p>
                  </div>

                  {/* Competitor side */}
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                    <div className="flex items-center gap-1.5 text-slate-700 font-extrabold text-[11px]">
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] text-white ${compVal.supported ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                        {compVal.supported ? '✓' : '✕'}
                      </span>
                      <span>{competitorMeta[mobileCompareTarget].name}</span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-snug">
                      {compVal.value}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile Action Bar */}
        <div className="p-4 bg-[#ffd64e] rounded-2xl text-center shadow-md space-y-2">
          <div className="text-sm font-black text-black">Start Exploring AbilityOS Now</div>
          <button
            onClick={() => onExploreDemo && onExploreDemo('modules')}
            className="w-full py-3 bg-[#1c1b1f] text-white rounded-xl font-extrabold text-xs flex items-center justify-center gap-2 shadow"
          >
            <span>Launch Live Interactive Sandbox</span>
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

      </div>

    </section>
  );
};
